import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Table state lives in the URL, not in component state. The point is shareability:
 * a filtered, sorted table becomes a link you can paste in Slack, and the back
 * button walks your filtering.
 *
 * Values equal to their default are REMOVED from the query string, so the common
 * case stays a clean `/repos` rather than `/repos?q=&type=&sort=name&dir=asc&page=1`.
 *
 * All setters use `replace` so adjusting a filter doesn't push a history entry per
 * keystroke — typing in a name filter would otherwise take twenty presses of Back
 * to escape.
 */
/**
 * `pageKey` is the page-number parameter this filter invalidates. It only needs
 * passing when a route hosts TWO independently paged tables (Network → Peers has
 * employee peers and the server-paginated device fleet): they share one address
 * bar, so the second table's filters must clear `dpage`, not `page`. Narrowing
 * one table while the other's page number silently survives leaves you looking
 * at offset 80 of a three-row result.
 */
export function useUrlState(
  key: string,
  def = '',
  pageKey = 'page',
): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? def;
  const set = useCallback(
    (v: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === def || v === '') next.delete(key);
          else next.set(key, v);
          // Any filter change invalidates the current page number.
          if (key !== pageKey) next.delete(pageKey);
          return next;
        },
        { replace: true },
      );
    },
    [key, def, pageKey, setParams],
  );
  return [value, set];
}

/**
 * Clear several params in ONE setParams call.
 *
 * Calling N `useUrlState` setters back-to-back does NOT work: each builds its next
 * URLSearchParams from the same `prev` (the location has not re-rendered between
 * them), so every call but the last is discarded. A "Clear filters" button wired as
 * `setQ(''); setLane(''); setLevel('')` therefore clears exactly one filter — the
 * same trap documented on `useUrlSort` below, which is why that hook writes its two
 * keys together.
 */
export function useUrlClear(keys: string[], pageKey = 'page'): () => void {
  const [, setParams] = useSearchParams();
  const joined = keys.join(',');
  return useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of joined.split(',')) next.delete(k);
        next.delete(pageKey);
        return next;
      },
      { replace: true },
    );
  }, [joined, pageKey, setParams]);
}

export function useUrlFlag(key: string, pageKey = 'page'): [boolean, (v: boolean) => void] {
  const [raw, setRaw] = useUrlState(key, '', pageKey);
  return [raw === '1', useCallback((v: boolean) => setRaw(v ? '1' : ''), [setRaw])];
}

/** A page number is its own `pageKey`, so setting it never clears itself. */
export function useUrlNumber(key: string, def: number): [number, (v: number) => void] {
  const [raw, setRaw] = useUrlState(key, String(def), key);
  const n = Number(raw);
  return [
    Number.isFinite(n) && n > 0 ? n : def,
    useCallback((v: number) => setRaw(String(v)), [setRaw]),
  ];
}

export type SortDir = 'asc' | 'desc';

export interface UrlSort {
  key: string;
  dir: SortDir;
  /** Click a header: same column flips direction, a new column starts ascending. */
  toggle: (k: string) => void;
}

/**
 * Sort key + direction are ONE logical change, so `toggle` writes both in a
 * SINGLE setParams call.
 *
 * Composing two `useUrlState` setters here did not work: each builds its next
 * URLSearchParams from the same `prev`, so the second call discarded the first's
 * edit. Clicking a new column produced "drop dir, keep the old sort" — the sort
 * never moved. Don't split this back into two setters.
 */
export function useUrlSort(defKey: string, defDir: SortDir = 'asc', prefix = ''): UrlSort {
  const [params, setParams] = useSearchParams();
  // `prefix` namespaces the three keys so two sortable tables can share a route
  // (Atlas IPs shows the live access list and the reported set side by side).
  // Unprefixed stays `sort`/`dir`/`page`, so every existing table is untouched.
  const sortKey = `${prefix}sort`;
  const dirKey = `${prefix}dir`;
  const pageKey = `${prefix}page`;
  const key = params.get(sortKey) ?? defKey;
  const rawDir = params.get(dirKey) ?? defDir;
  const dir: SortDir = rawDir === 'desc' ? 'desc' : 'asc';

  const toggle = useCallback(
    (k: string) => {
      // same column flips direction; a new column starts ascending
      const nextDir: SortDir = k === key ? (dir === 'asc' ? 'desc' : 'asc') : 'asc';
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (k === defKey) next.delete(sortKey);
          else next.set(sortKey, k);
          if (nextDir === defDir) next.delete(dirKey);
          else next.set(dirKey, nextDir);
          next.delete(pageKey); // re-sorting invalidates the page number
          return next;
        },
        { replace: true },
      );
    },
    [key, dir, defKey, defDir, sortKey, dirKey, pageKey, setParams],
  );

  return { key, dir, toggle };
}

/**
 * Comparator for the small in-memory datasets these pages hold (~1.9k rows at
 * most, already fully loaded). Sorting client-side keeps the GraphQL schema
 * untouched, which is what the redesign brief asked for.
 *
 * Nullish always sorts last regardless of direction — "no owner" belongs at the
 * bottom whichever way you're reading the column.
 */
export function compareBy<T>(rows: T[], pick: (r: T) => unknown, dir: SortDir): T[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = pick(a);
    const y = pick(b);
    const xEmpty = x === null || x === undefined || x === '';
    const yEmpty = y === null || y === undefined || y === '';
    if (xEmpty || yEmpty) return xEmpty && yEmpty ? 0 : xEmpty ? 1 : -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    if (typeof x === 'boolean' && typeof y === 'boolean') return (Number(x) - Number(y)) * sign;
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign;
  });
}
